import type { WorkflowChangeSet, WorkflowChangeSetItem } from '@/api/workflowChangeSets'

export function currentChangeSetOf(changeSets: readonly WorkflowChangeSet[]): WorkflowChangeSet | undefined {
  return changeSets.find((c) => c.status === 'Pending') ?? changeSets[changeSets.length - 1]
}

export function isReviewableRound(changeSets: readonly WorkflowChangeSet[], changeSet: WorkflowChangeSet): boolean {
  const current = currentChangeSetOf(changeSets)
  return !!current
    && current.status === 'Pending'
    && changeSet.producingPhaseId === current.producingPhaseId
    && (changeSet.status === 'Pending' || changeSet.status === 'Superseded')
}

export function stagedRoundOf(changeSets: readonly WorkflowChangeSet[]): { changeSet: WorkflowChangeSet; count: number } | undefined {
  const changeSet = changeSets
    .filter((c) => isReviewableRound(changeSets, c) && c.items.some((i) => i.decision === 'Staged'))
    .reduce<WorkflowChangeSet | undefined>((newest, c) => (!newest || c.round > newest.round ? c : newest), undefined)
  return changeSet && { changeSet, count: changeSet.items.filter((i) => i.decision === 'Staged').length }
}

export function stagedCountLabel(stagedRound: ReturnType<typeof stagedRoundOf>, current: WorkflowChangeSet): string {
  if (!stagedRound) return '0 staged'
  return stagedRound.changeSet.id === current.id
    ? `${stagedRound.count} staged`
    : `${stagedRound.count} staged (Round ${stagedRound.changeSet.round})`
}

export interface ChangeSetDiffBaseline {
  beforeValuesJson: string | null
  fromPreviousRound: boolean
  label: string
}

function previousRoundOf(changeSets: readonly WorkflowChangeSet[], changeSet: WorkflowChangeSet) {
  return changeSets.find((c) => c.producingPhaseId === changeSet.producingPhaseId && c.round === changeSet.round - 1)
}

// The server reuses a Create item's target id only when its title is unchanged, and a Document's title tracks its first heading.
function matchingPreviousItem(previous: WorkflowChangeSet, current: WorkflowChangeSet, item: WorkflowChangeSetItem) {
  const sameShape = (i: WorkflowChangeSetItem) => i.entityKind === item.entityKind && i.operation === item.operation
  const candidates = previous.items.filter(sameShape)
  const byTarget = candidates.find((i) => i.targetEntityId === item.targetEntityId)
  if (byTarget || item.operation !== 'Create') return byTarget
  return candidates.length === 1 && current.items.filter(sameShape).length === 1 ? candidates[0] : undefined
}

function parseObject(json: string | null): Record<string, unknown> {
  if (!json) return {}
  try {
    const parsed = JSON.parse(json) as unknown
    return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

export function changeSetDiffBaseline(
  changeSets: readonly WorkflowChangeSet[],
  changeSet: WorkflowChangeSet,
  item: WorkflowChangeSetItem,
): ChangeSetDiffBaseline {
  const previous = previousRoundOf(changeSets, changeSet)
  const previousItem = previous && matchingPreviousItem(previous, changeSet, item)

  if (previous && previousItem) {
    const merged = { ...parseObject(item.beforeValuesJson), ...parseObject(previousItem.proposedValuesJson) }
    return { beforeValuesJson: JSON.stringify(merged), fromPreviousRound: true, label: `Round ${previous.round}` }
  }

  return { beforeValuesJson: item.beforeValuesJson, fromPreviousRound: false, label: 'Current' }
}
